import {
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { computeReadableValue } from "../utils/strings";
import { formatDate, formatFullValueWithLocalDates } from "../utils/dates";
import LightTooltip from "./LightTooltip";
import { AggregatedItem } from "../utils/dataAggregation";
import { createStyleSheet } from "../utils/styles";

const styles = createStyleSheet({
  tooltipDescription: {
    marginBottom: 1,
  },
  descriptionTextContainer: {
    paddingLeft: 2,
    color: "GrayText",
  },
  plainCell: {
    padding: 0,
    borderBottom: "none",
    verticalAlign: "top",
  },
});

interface AggregatedFieldRowProps {
  fieldName: string;
  description: string;
  aggregatedItems: AggregatedItem[];
}

/**
 * Component for rendering a single aggregated field row in the collected data table
 * Handles display formatting and tooltip with all aggregated items
 */
export const AggregatedFieldRow: React.FC<AggregatedFieldRowProps> = ({
  fieldName,
  description,
  aggregatedItems,
}) => {

  if (aggregatedItems.length === 0) return null;

  // Group items by name, keeping track of dates
  const groupedItems = new Map<
    string,
    Array<{ date: Date; fullValue: string }>
  >();
  aggregatedItems.forEach((item) => {
    if (!groupedItems.has(item.name)) {
      groupedItems.set(item.name, []);
    }
    groupedItems.get(item.name)!.push({
      date: item.date,
      fullValue: item.fullValue,
    });
  });

  const displayItems = Array.from(groupedItems.entries())
    .map(([name, occurrences]) => {
      const mostRecent = occurrences.sort(
        (a, b) => b.date.getTime() - a.date.getTime()
      )[0];
      return {
        name,
        date: mostRecent.date,
        formattedDate: formatDate(mostRecent.date),
      };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  const tooltipContent = (
    <Stack direction="column" spacing={0.5}>
      {description && description !== fieldName && (
        <Typography variant="tooltip" sx={styles.tooltipDescription}>
          {description}
        </Typography>
      )}
      <Typography
        variant="tooltip"
        component="ul"
        sx={{
          margin: 0,
          paddingLeft: 2,
          maxWidth: 400,
          listStyleType: "disc",
        }}
      >
        {aggregatedItems.map((item, idx) => {
          const formatted = formatFullValueWithLocalDates(item.fullValue);
          const parts = formatted.split(/([()])/);
          let isBold = true;
          return (
            <li key={`${item.name}-${idx}`} style={{ marginBottom: "4px" }}>
              {parts.map((part, partIdx) => {
                if (part === "(") {
                  isBold = false;
                  return <span key={partIdx}>{part}</span>;
                }
                if (part === ")") {
                  isBold = true;
                  return <span key={partIdx}>{part}</span>;
                }
                return (
                  <Typography
                    key={partIdx}
                    component="span"
                    variant="tooltip"
                    sx={isBold ? { fontWeight: 600 } : {}}
                  >
                    {part}
                  </Typography>
                );
              })}
            </li>
          );
        })}
      </Typography>
    </Stack>
  );

  return (
    <LightTooltip
      title={tooltipContent}
      disableInteractive
      placement="bottom-start"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -12],
              },
            },
          ],
        },
      }}
    >
      <TableRow sx={{ width: "100%" }}>
        <TableCell sx={styles.plainCell}>
          <Typography component="ul" sx={styles.descriptionTextContainer}>
            <Typography
              variant="body2"
              component="li"
              sx={{ listStyleType: "disc" }}
            >
              {computeReadableValue(fieldName)}:
            </Typography>
          </Typography>
        </TableCell>
        <TableCell sx={styles.plainCell}>
          <Typography variant="body2" sx={{ marginLeft: 1 }}>
            {displayItems.map((item, idx) => (
              <React.Fragment key={item.name}>
                {idx > 0 && ", "}
                <Typography component="span" variant="body2bold">
                  {item.name}
                </Typography>
                <Typography component="span" variant="body2">
                  {" "}({item.formattedDate})
                </Typography>
              </React.Fragment>
            ))}
          </Typography>
        </TableCell>
      </TableRow>
    </LightTooltip>
  );
};
